import React from 'react'
import PropTypes from 'prop-types'
import { Box, Collapse, Button, Grid, Text, useTheme } from '@deseretbook/react-ui'
import Clock from '@deseretbook/icons/react/clock'
import { ClickableContent } from '../../../../core'
import { helpers } from '../../../../../helpers'

/**
 * Layout for individual lessons
 *
 * @param {number} position - Order lesson appears in list
 * @param {string} title - Lesson title
 * @param {number} duration - lesson duration in seconds
 * @param {string} description - lesson description
 * @param {boolean} isActive - Whether the current track is active
 *
 * @returns {function} React Component
 */
export const Lesson = ({
  position,
  title,
  duration,
  description,
  isActive,
  updateActiveLesson,
}) => {
  const [isCollapsed, setIsCollapsed] = React.useState(true)
  const theme = useTheme()

  return (
    <Box
      padding={0.5}
      paddingLeft={0}
      background={isActive ? 'alternate' : 'main'}
      style={{
        borderTop: '1px solid rgba(0, 0, 0, 0.3)',
        borderLeft: `6px solid ${isActive ? theme.palette.seek.primary.main : 'transparent'}`,
        backgroundColor: isActive ? theme.palette.seek.primary.light : 'white',
        color: isActive ? 'white' : 'black',
      }}
    >
      <ClickableContent
        onClick={updateActiveLesson}
        isPaddingless
        color={isActive ? theme.palette.seek.primary.main : null}
      >
        <Grid
          columns={[2, Grid.fr(1), 3]}
          rows={[2.5, 1]}
          gap={0.5}
          areas={['position title collapse', '. runtime .']}
          alignItems="start"
        >
          <Grid.Item area="position" justify="right">
            {position < 10 ? `0${position}` : position}.
          </Grid.Item>
          <Grid.Item area="title">{title}</Grid.Item>
          <Grid.Item area="runtime">
            <Box alignItems="center">
              <Box display="inline" marginRight={0.5}>
                <Clock color={theme.palette[isActive ? 'light' : 'secondary'].main} />
              </Box>
              <Text
                isBold={isActive}
                size="small"
                color={isActive ? 'light' : 'secondary'}
                shade="light"
              >
                {helpers.formatDuration(duration)}
              </Text>
            </Box>
          </Grid.Item>
          <Grid.Item area="collapse">
            <Button
              size="small"
              icon={`chevron${isCollapsed ? 'Down' : 'Up'}`}
              onClick={(event) => {
                // Prevent activating the clickableContent
                event.stopPropagation()
                setIsCollapsed(!isCollapsed)
              }}
              aria-label={`${isCollapsed ? 'Show' : 'Hide'} lesson info`}
            />
          </Grid.Item>
        </Grid>
      </ClickableContent>
      <Collapse isCollapsed={isCollapsed}>
        <Box marginTop={1} padding={0.5}>
          <Text color={isActive ? 'light' : 'secondary'}>{description}</Text>
        </Box>
      </Collapse>
    </Box>
  )
}

Lesson.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  position: PropTypes.number,
  duration: PropTypes.number,
  isActive: PropTypes.bool,
  updateActiveLesson: PropTypes.func,
}
